/** PACK PREFETCH, CLIENT ONLY (Tom, 2026-10-19: "Right, so let's do it"):
 *  when a line this page views gets a new commit — mine or anyone's — the
 *  packs of that commit's snapshot are fetched into the pack cache in idle
 *  time, so the next COLD open (a dropped island store, another device, an
 *  evicted entry) downloads nothing. No server announcement: the existing
 *  manifest route, the existing loader, the existing pack cache.
 *
 *  - Coalesced per (island, line): a newer commit replaces a waiting one.
 *  - After DELAY_MS, in idle time. The manifest route answers an OLDER base
 *    while the snapshot writer is behind: then asked again after each of
 *    RETRY_MS, and given up silently.
 *  - The loader fetches only the packs the cache lacks, sharing any fetch a
 *    load has in flight; its state is thrown away: the live view is never
 *    touched.
 *  - Only for a persistent pack cache (an organisation, a User node id, not
 *    impersonating): memory alone dies with the page.
 *  - Every failure is silent: the next cold open fetches what it needs.
 *    Signed links are never logged. */

import { loadStateFromPacks, type StateManifest } from "./loader";
import type { PackCache } from "./pack-cache";

export const PREFETCH_DELAY_MS = 5_000;
export const PREFETCH_RETRY_MS = [15_000, 45_000];

type Want = { island: string; line: string; commit: string; tries: number };

export type PrefetchOptions = {
  /** the REST base, or null when there is none */
  base: () => string | null;
  /** the pack cache now (it is chosen anew with who the data is read as) */
  cache: () => PackCache;
  fetch?: typeof fetch;
  delayMs?: number;
  retryMs?: number[];
  /** run `fn` when the browser is idle */
  idle?: (fn: () => void) => void;
};

const browserIdle = (fn: () => void) => {
  const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
  if (typeof ric === "function") ric(fn, { timeout: 10_000 });
  else setTimeout(fn, 0);
};

/** A draft or a proposal has no manifest (the route answers 404). */
const isLine = (lane: string) => !/^(wip|proposal)\//.test(lane);

export class PackPrefetcher {
  private wants = new Map<string, Want>();
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  private running = new Set<string>();
  private disposed = false;

  constructor(private opts: PrefetchOptions) {}

  /** The line `line` of `island` has commit `commit`: prefetch its packs. */
  want(island: string, line: string | null | undefined, commit: string | null | undefined): void {
    if (this.disposed || !island || !line || !commit || !isLine(line)) return;
    if (!this.opts.cache().persistent) return;
    const key = `${island}|${line}`;
    const held = this.wants.get(key);
    if (held?.commit === commit) return;
    this.wants.set(key, { island, line, commit, tries: 0 });
    this.arm(key, this.opts.delayMs ?? PREFETCH_DELAY_MS);
  }

  /** Forget everything waiting (who the data is read as changed). */
  clear(): void {
    for (const t of Array.from(this.timers.values())) clearTimeout(t);
    this.timers.clear();
    this.wants.clear();
  }

  dispose(): void {
    this.disposed = true;
    this.clear();
  }

  private arm(key: string, ms: number): void {
    const t = this.timers.get(key);
    if (t) clearTimeout(t);
    this.timers.set(key, setTimeout(() => {
      this.timers.delete(key);
      (this.opts.idle ?? browserIdle)(() => { void this.run(key); });
    }, ms));
  }

  private async run(key: string): Promise<void> {
    const want = this.wants.get(key);
    if (this.disposed || !want || this.running.has(key)) return;
    const base = this.opts.base();
    const cache = this.opts.cache();
    if (!base || !cache.persistent) { this.wants.delete(key); return; }
    this.running.add(key);
    const f = this.opts.fetch ?? fetch;
    const url = `${base}/versioning/state/${encodeURIComponent(want.island)}/${encodeURIComponent(want.line)}/${encodeURIComponent(want.commit)}/manifest.json`;
    const manifestOf = async (ask?: { missing?: string[] }): Promise<StateManifest> => {
      const missing = (ask?.missing ?? []).map((n) => `missing=${encodeURIComponent(n)}`).join("&");
      const r = await f(missing ? `${url}?${missing}` : url, { credentials: "include" });
      if (!r.ok) throw new Error(`manifest ${r.status}`); // never the link: no detail
      return r.json();
    };
    let retry = false;
    try {
      const manifest = await manifestOf();
      if (this.wants.get(key) !== want) return; // a newer commit came meanwhile
      if (manifest.commit !== want.commit) {
        // the writer has not stored this commit's snapshot yet: an older base
        retry = true;
        return;
      }
      await loadStateFromPacks({ initial: manifest, getManifest: manifestOf, cache, fetch: f });
    } catch {
      /* silent: the next cold open fetches what it needs */
    } finally {
      this.running.delete(key);
      if (this.wants.get(key) === want) {
        const wait = retry ? (this.opts.retryMs ?? PREFETCH_RETRY_MS)[want.tries] : undefined;
        if (wait !== undefined && !this.disposed) {
          want.tries++;
          this.arm(key, wait);
        } else {
          this.wants.delete(key);
        }
      } else if (this.wants.has(key) && !this.timers.has(key)) {
        // a newer commit arrived while this one ran: its own delay
        this.arm(key, this.opts.delayMs ?? PREFETCH_DELAY_MS);
      }
    }
  }
}
